// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// Custom Component that will use the CustomProductContext

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xngUseWidgetBadges from "@tps/App/Hooks/xngUseWidgetBadges";
import { CustomHasProductContext, withProductContext } from "@tps/App/Store/Contexts/CustomProductContext";
import ProductImage, { ProductImageStyles } from "@tps/Overrides/Components/ProductImage";
import { mergeWithCss } from "@tps/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    showImage: boolean;
    extendedStyles?: ProductListProductImageStyles;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    onClick?: (isNewPageWasOpen: boolean) => void;
}

type Props = OwnProps & CustomHasProductContext;

export interface ProductListProductImageStyles {
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
}

export const productImageStyles: ProductListProductImageStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    productImage: {
        image: {
            width: "80px",
        },
    },
};

const CustomProductImage = ({
    productContext,
    showImage,
    extendedStyles,
    showImageBadges,
    maxImageBadges,
    onClick,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productImageStyles, extendedStyles));
    const { imageBadges } = xngUseWidgetBadges({ productContext, showTextBadges: false, showImageBadges });

    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="productListProductImage"
            data-test-key={productContext.product.id}
        >
            {showImage && (
                <ProductImage
                    extendedStyles={styles.productImage}
                    product={productContext}
                    redirectToParent={true}
                    {...(onClick && onClick)}
                />
            )}
            {showImage && showImageBadges && imageBadges && (
                <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
            )}
        </StyledWrapper>
    );
};

export default withProductContext(CustomProductImage);
